import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  Search,
  Filter,
  Calendar,
  Pencil,
  XCircle,
  MessageSquarePlus,
  Video,
  Phone,
  MapPin,
  Users,
} from 'lucide-react';
import { fetchInterviews, cancelInterview } from '@/lib/api';
import type { Interview } from '@/types';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { InterviewEditDialog } from './InterviewEditDialog';
import { InterviewFeedbackForm } from './InterviewFeedbackForm';
import { formatDateTime, formatStatusLabel } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' },
];

const TIME_FILTER_OPTIONS = [
  { value: 'all', label: 'All Interviews' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'past', label: 'Past' },
];

function getStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'scheduled':
    case 'confirmed':
      return 'default';
    case 'in_progress':
      return 'warning';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'destructive';
    case 'no_show':
      return 'destructive';
    default:
      return 'secondary';
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'video':
      return <Video className="h-3.5 w-3.5" />;
    case 'phone':
      return <Phone className="h-3.5 w-3.5" />;
    case 'onsite':
      return <MapPin className="h-3.5 w-3.5" />;
    case 'panel':
      return <Users className="h-3.5 w-3.5" />;
    default:
      return <Calendar className="h-3.5 w-3.5" />;
  }
}

interface InterviewListProps {
  /** Optional: filter to a specific candidate */
  candidateId?: string;
  /** Optional: filter to a specific job */
  jobId?: string;
  /** Whether to show the search bar (disabled on candidate/job detail pages) */
  showSearch?: boolean;
}

export function InterviewList({
  candidateId,
  jobId,
  showSearch = true,
}: InterviewListProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const pageSize = 10;

  // Cancel confirmation dialog state
  const [cancelTarget, setCancelTarget] = useState<Interview | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Edit dialog state
  const [editTarget, setEditTarget] = useState<Interview | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Feedback dialog state
  const [feedbackTarget, setFeedbackTarget] = useState<Interview | null>(null);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const params = useMemo(() => {
    const p: {
      page: number;
      limit: number;
      candidateId?: string;
      jobId?: string;
      status?: string;
      upcoming?: boolean;
    } = {
      page,
      limit: pageSize,
    };

    if (candidateId) p.candidateId = candidateId;
    if (jobId) p.jobId = jobId;
    if (statusFilter !== 'all') p.status = statusFilter;
    if (timeFilter === 'upcoming') p.upcoming = true;

    return p;
  }, [page, candidateId, jobId, statusFilter, timeFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['interviews', params, debouncedSearch],
    queryFn: () => fetchInterviews(params),
  });

  // Client-side filtering for search + past filter
  const filteredData = useMemo(() => {
    let items = data?.data ?? [];

    // Past filter: only show interviews in the past
    if (timeFilter === 'past') {
      const now = new Date();
      items = items.filter((i) => new Date(i.scheduledAt) < now);
    }

    // Search filter
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      items = items.filter(
        (i) =>
          i.title.toLowerCase().includes(q) ||
          `${i.candidate.firstName} ${i.candidate.lastName}`
            .toLowerCase()
            .includes(q) ||
          i.job.title.toLowerCase().includes(q),
      );
    }

    return items;
  }, [data?.data, debouncedSearch, timeFilter]);

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelInterview(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      addToast({ type: 'success', title: 'Interview cancelled' });
      setShowCancelDialog(false);
      setCancelTarget(null);
    },
    onError: (error: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to cancel interview',
        message: error.message,
      });
    },
  });

  const handleCancelClick = (interview: Interview) => {
    setCancelTarget(interview);
    setShowCancelDialog(true);
  };

  const confirmCancel = () => {
    if (cancelTarget) {
      cancelMutation.mutate(cancelTarget.id);
    }
  };

  const handleEditClick = (interview: Interview) => {
    setEditTarget(interview);
    setShowEditDialog(true);
  };

  const handleFeedbackClick = (interview: Interview) => {
    setFeedbackTarget(interview);
    setShowFeedbackDialog(true);
  };

  const canCancel = (interview: Interview) =>
    interview.status !== 'cancelled' && interview.status !== 'completed';

  const canEdit = (interview: Interview) =>
    interview.status !== 'cancelled' && interview.status !== 'completed';

  const canAddFeedback = (interview: Interview) =>
    (interview.status === 'completed' || interview.status === 'in_progress') &&
    !interview.feedback;

  const columns: ColumnDef<Interview>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Interview',
        cell: ({ row }) => {
          const interview = row.original;
          return (
            <div className="space-y-1">
              <div className="font-medium">{interview.title}</div>
              <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
                {getTypeIcon(interview.type)}
                <span className="capitalize">{interview.type}</span>
                <span>&middot;</span>
                <span>{interview.durationMinutes} min</span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'candidate',
        header: 'Candidate',
        cell: ({ row }) => {
          const interview = row.original;
          return (
            <Link
              to={`/candidates/${interview.candidateId}`}
              className="font-medium hover:text-emerald-500"
            >
              {interview.candidate.firstName} {interview.candidate.lastName}
            </Link>
          );
        },
      },
      {
        accessorKey: 'job',
        header: 'Job',
        cell: ({ row }) => {
          const interview = row.original;
          return (
            <Link
              to={`/jobs/${interview.jobId}`}
              className="text-sm hover:text-emerald-500"
            >
              {interview.job.title}
            </Link>
          );
        },
      },
      {
        accessorKey: 'scheduledAt',
        header: 'Scheduled',
        cell: ({ row }) => {
          const date = row.original.scheduledAt;
          const isPast = new Date(date) < new Date();
          return (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-emerald-400" />
              <span
                className={`text-sm ${isPast ? 'text-text-tertiary' : ''}`}
              >
                {formatDateTime(date)}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>
              {formatStatusLabel(status)}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'rating',
        header: 'Rating',
        cell: ({ row }) => {
          const rating = row.original.rating;
          if (rating == null)
            return <span className="text-text-tertiary text-sm">-</span>;
          return <span className="text-sm">{rating}/5</span>;
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const interview = row.original;
          return (
            <div className="flex items-center gap-1">
              {canEdit(interview) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditClick(interview)}
                  title="Edit interview"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {canAddFeedback(interview) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFeedbackClick(interview)}
                  title="Add feedback"
                >
                  <MessageSquarePlus className="h-4 w-4" />
                </Button>
              )}
              {canCancel(interview) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCancelClick(interview)}
                  title="Cancel interview"
                  className="text-red-400 hover:text-red-300"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [],
  );

  const useFiltered = debouncedSearch || timeFilter === 'past';

  return (
    <>
      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          {showSearch && (
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
              <Input
                placeholder="Search by title, candidate, or job..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          )}

          {/* Status Filter */}
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-tertiary" />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Time Filter (Upcoming / Past / All) */}
            <Select
              value={timeFilter}
              onValueChange={(value) => {
                setTimeFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Time" />
              </SelectTrigger>
              <SelectContent>
                {TIME_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <DataTable
          columns={columns}
          data={useFiltered ? filteredData : (data?.data ?? [])}
          page={page}
          pageSize={pageSize}
          total={
            useFiltered
              ? filteredData.length
              : (data?.pagination.total ?? 0)
          }
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No interviews found. Try adjusting your filters."
          getRowId={(row) => row.id}
        />
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancel Interview</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the interview{' '}
              <strong>{cancelTarget?.title}</strong>? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
            >
              Keep Interview
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Interview'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <InterviewEditDialog
        interview={editTarget}
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) setEditTarget(null);
        }}
      />

      {/* Feedback Dialog */}
      {feedbackTarget && (
        <InterviewFeedbackForm
          interviewId={feedbackTarget.id}
          interviewTitle={feedbackTarget.title}
          open={showFeedbackDialog}
          onOpenChange={(open) => {
            setShowFeedbackDialog(open);
            if (!open) setFeedbackTarget(null);
          }}
        />
      )}
    </>
  );
}
